import { Formik } from 'formik';
import AttachMoneyRoundedIcon from '@material-ui/icons/AttachMoneyRounded';

import {
  SButton,
  SETVIImage,
  SFormikTextfield
} from '@setvi/shared/components';
import { VALIDATORS } from '@setvi/shared/enums';
import {
  AnalyticsModules,
  useAnalytics,
  useCustomErrors
} from '@setvi/shared/hooks';
import { CheckIconV2 } from '@setvi/shared/components/sicons';

// import { UserStatusColor } from 'pages/admin/pages/users/enum';
// import { FlexRow } from 'pages/auto-rfq/components/product-card/styled';
import { useStyledTextFieldStyles } from 'pages/auto-rfq/pages/list/components/create-dialog/styled';

import { Product } from '@setvi/shared/interfaces';
import { useRfqId } from 'pages/auto-rfq/context/rfq-id';
import {
  manufacturerValidation,
  priceValidation,
  nameValidation,
  skuValidation,
  schema
} from './utils';
import { Container, Content, Footer, InputContainer } from './styled';

export interface ManualProps {
  url?: string;
  sku: string;
  name: string;
  price: string;
  manufacturer: string;
  existinInDb?: boolean;
  unitOfMeasure?: string;
}

interface ManualEntryProps {
  values?: ManualProps;
  product?: Product;
  isSingerUser?: boolean;
  handleSubmit: (props: ManualProps) => void;
  onClose: () => void;
}

const ManualEntry = ({
  product,
  values: importedValues,
  isSingerUser,
  onClose,
  handleSubmit
}: ManualEntryProps) => {
  const id = useRfqId();

  const { captureAnalytics } = useAnalytics();
  const { field } = useStyledTextFieldStyles();

  const values: ManualProps = {
    url: importedValues?.url || '',
    sku: importedValues?.sku || '',
    name: importedValues?.name || '',
    price: importedValues?.price || '',
    manufacturer: importedValues?.manufacturer || '',
    existinInDb: importedValues?.existinInDb || false,
    unitOfMeasure: importedValues?.unitOfMeasure || ''
  };

  const { customErrors, customValidate } = useCustomErrors({
    schemas: schema,
    values
  });

  const disabled = (props: ManualProps, validators: any) => {
    if (
      !props?.name?.trim() ||
      !props?.sku?.trim() ||
      !props?.manufacturer?.trim()
    )
      return true;

    const hasValidationErrors = ['name', 'sku', 'manufacturer', 'price'].some(
      key => validators?.[key]?.length > 0
    );

    if (hasValidationErrors) return true;

    return false;
  };

  return (
    <Formik
      initialValues={values}
      onSubmit={(v: ManualProps) => {
        handleSubmit(v);

        const sourceIsPL = product?.isPrivateLabel || product?.privateLabel;

        captureAnalytics(
          {
            action: 'manual',
            rfqId: id || '',
            sourceProductId: product?.sku || '',
            sourceIsPL
          },
          AnalyticsModules.AutoRFQItem
        );
      }}>
      {({ values: formikValues }) => (
        <Container>
          <Content>
            <SETVIImage
              src="/images/add-tag.png"
              alt="Add Item"
              height={250}
              width={250}
              style={{
                marginTop: '10vh'
              }}
            />

            <InputContainer>
              <SFormikTextfield
                name="name"
                label="Product Name"
                placeholder="Enter a product name"
                required
                maxLength={VALIDATORS.NAME_MAX_LENGTH}
                onChange={(value: string) =>
                  customValidate({
                    schema: nameValidation,
                    value,
                    name: 'name'
                  })
                }
                inputClass={field}
                customErrors={customErrors}
              />

              <SFormikTextfield
                name="manufacturer"
                label="Manufacturer Name"
                placeholder="Enter a manufacturer name"
                required
                maxLength={VALIDATORS.NAME_MAX_LENGTH}
                onChange={(value: string) =>
                  customValidate({
                    schema: manufacturerValidation,
                    value,
                    name: 'manufacturer'
                  })
                }
                inputClass={field}
                customErrors={customErrors}
              />

              <SFormikTextfield
                name="sku"
                label="SKU"
                placeholder="Enter SKU"
                required
                maxLength={VALIDATORS.NAME_MAX_LENGTH}
                onChange={(value: string) =>
                  customValidate({
                    schema: skuValidation,
                    value,
                    name: 'sku'
                  })
                }
                inputClass={field}
                customErrors={customErrors}
              />

              <SFormikTextfield
                name="price"
                label="Price"
                placeholder="Enter price"
                startIcon={<AttachMoneyRoundedIcon htmlColor="#5D7083" />}
                onChange={(value: string) =>
                  customValidate({
                    schema: priceValidation,
                    value,
                    name: 'price'
                  })
                }
                inputClass={field}
                customErrors={customErrors}
              />

              {isSingerUser && (
                <SFormikTextfield
                  name="unitOfMeasure"
                  label="UOM"
                  placeholder="Enter Unit of Measure"
                  inputClass={field}
                />
              )}

              {/* values?.existinInDb && (
                <GrayBox mt={2}>
                  <SText>
                    This manufacturer{' '}
                    <b
                      style={{
                        color: UserStatusColor.Active
                      }}>
                      exists
                    </b>{' '}
                    in our database.
                  </SText>
                </GrayBox>
              ) */}
              {/* values?.url && (
                <GrayBox>
                  <a href={values?.url} target="_blank" rel="noreferrer">
                    <FlexRow
                      style={{
                        height: '100%',
                        width: '100%',
                        padding: '12px 0px 12px',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                      <SText fontColor="#000">View Product Online</SText>
                      <ExternalLinkIcon
                        style={{
                          width: 16
                        }}
                      />
                    </FlexRow>
                  </a>
                </GrayBox>
              ) */}
            </InputContainer>
          </Content>

          <Footer>
            <SButton
              color="default"
              variant="text"
              onClick={onClose}
              style={{
                borderRadius: 36,
                color: '#5D7083'
              }}>
              Cancel
            </SButton>

            <SButton
              type="submit"
              endIcon={
                <CheckIconV2
                  style={{
                    width: 20,
                    height: 20,
                    marginBottom: 2
                  }}
                />
              }
              disabled={disabled(formikValues, customErrors)}
              style={{
                borderRadius: 36
              }}>
              Add Item
            </SButton>
          </Footer>
        </Container>
      )}
    </Formik>
  );
};

export default ManualEntry;
